/**
 * Weekly Agenda Event Card Component
 *
 * Event card for the weekly agenda grid, styled to match day view cards.
 * Shows colored border, shadow, tinted background, time, and wrapping title.
 *
 * Follows single responsibility: renders one event card only.
 */

import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Colors } from '@/constants/theme';
import { getBodyFont } from '@/constants/typography';
import { getLightBackground, getDarkBackground } from '@/lib/calendar/EventDisplayUtils';
import { getContrastTextColor } from '@/lib/utils/color-contrast';
import { useColorScheme } from '@/hooks/use-color-scheme';
import type { CalendarEvent } from '@/types/events';
import { TaskCountResolver } from '@/lib/utils/TaskCountResolver';
import { TaskIndicator } from '@/components/shared/TaskIndicator';

type WeeklyAgendaEventCardProps = {
  event: CalendarEvent;
  memberColor: string;
  onPress?: (event: CalendarEvent) => void;
};

export const WeeklyAgendaEventCard = React.memo(function WeeklyAgendaEventCard({
  event,
  memberColor,
  onPress,
}: WeeklyAgendaEventCardProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  const isDarkMode = colorScheme === 'dark';
  const isAllDay = event.allDay === true;
  const timeLabel = isAllDay
    ? 'All day'
    : event.startTime ?? '';

  const fadedColor = isDarkMode ? getDarkBackground(memberColor) : getLightBackground(memberColor);

  const cardStyle = isAllDay
    ? { backgroundColor: fadedColor, borderWidth: 0 }
    : { backgroundColor: 'transparent', borderColor: memberColor };

  const contrastColor = getContrastTextColor(fadedColor);

  const textColor = isAllDay ? contrastColor : palette.text;
  const mutedTextColor = isAllDay ? contrastColor : palette.textMuted;

  const taskCount = TaskCountResolver.resolve(event.description);

  return (
    <TouchableOpacity
      style={[
        styles.card,
        cardStyle,
      ]}
      onPress={() => onPress?.(event)}
      activeOpacity={0.7}
      disabled={!onPress}
    >
      {taskCount && (
        <View style={styles.taskIndicatorOverlay}>
          <TaskIndicator taskCount={taskCount} size="medium" />
        </View>
      )}
      {timeLabel !== '' && (
        <Text style={[styles.timeText, { color: mutedTextColor }]} numberOfLines={1}>
          {timeLabel}
        </Text>
      )}
      <Text style={[styles.titleText, { color: textColor }]}>
        {event.title}
      </Text>
    </TouchableOpacity>
  );
});

const styles = StyleSheet.create({
  card: {
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 10,
    paddingVertical: 8,
    justifyContent: 'center',
    marginBottom: 4,
  },
  timeText: {
    fontSize: 10,
    fontWeight: '500',
    marginBottom: 1,
  },
  titleText: {
    fontSize: 12,
    fontWeight: '600',
    fontFamily: getBodyFont(),
  },
  taskIndicatorOverlay: {
    position: 'absolute',
    top: -5,
    right: -5,
    zIndex: 1,
  },
});
